import { useEffect, useState } from "react";
import { api, getToken } from "../lib/api";
import { PageShell } from "./PageShell";
import { ConsoleIcon } from "./ConsoleIcon";

const number = (value) => Number(value || 0).toLocaleString();

function EmptyState({ icon, title, children, action, onAction }) {
  return <div className="dash-empty">
    <ConsoleIcon name={icon} size={26}/>
    <h3>{title}</h3><p>{children}</p>
    {action && <button className="btn btn-outline" onClick={onAction}>{action}</button>}
  </div>;
}

export function Dashboard({ onNavigate }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [revision, setRevision] = useState(0);
  const [updated, setUpdated] = useState(null);
  const authenticated = Boolean(getToken());

  useEffect(() => {
    if (!authenticated) return;
    let active = true;
    let inFlight = false;
    const load = async () => {
      if (inFlight) return;
      inFlight = true;
      setLoading(true);
      try {
        const response = await api.analytics();
        if (active) { setData(response); setError(""); setUpdated(new Date()); }
      } catch {
        if (active) setError("We couldn't refresh workspace analytics. Please try again.");
      } finally {
        inFlight = false;
        if (active) setLoading(false);
      }
    };
    load();
    const timer = setInterval(load, 30000);
    return () => { active = false; clearInterval(timer); };
  }, [authenticated, revision]);

  const ready = authenticated && data !== null;
  const series = ready ? (data.queries_over_time || []).slice(-20) : [];
  const topKbs = ready ? [...(data.queries_by_kb || [])].sort((a, b) => b.queries - a.queries).slice(0, 5) : [];
  const needsFirstSource = ready && !(data.queries_by_kb || []).length;
  const keys = ready ? (data.api_keys || []).slice(0, 5) : [];
  const maxQueries = Math.max(1, ...series.map(row => Number(row.count) || 0));
  const maxKb = Math.max(1, ...topKbs.map(row => Number(row.queries) || 0));
  const avgResponse = ready && data.total_queries > 0 && data.avg_latency_ms != null ? `${(Number(data.avg_latency_ms) / 1000).toFixed(2)}s` : "—";
  const lastQueryDay = series.length ? series[series.length - 1].date : ready ? "No recorded queries" : authenticated ? error ? "Unavailable" : "Loading" : "Sign in required";
  const statusItems = [
    { label: "Report scope", value: authenticated ? "Your account" : "Preview mode", tone: "neutral" },
    { label: "Analytics", value: error ? ready ? "Stale data" : "Unavailable" : ready ? "Available" : authenticated ? "Loading" : "Sign in required", tone: error ? "bad" : ready ? "good" : "neutral" },
    { label: "Last query", value: lastQueryDay, tone: series.length && !error ? "good" : "neutral" },
  ];
  const metrics = [
    { label: "Total queries", value: ready ? number(data.total_queries) : "—", detail: "Recorded requests", icon: "search" },
    { label: "Tokens used", value: ready ? number(data.total_tokens) : "—", detail: "Your recorded queries", icon: "analytics" },
    { label: "Active API keys", value: ready ? number(data.active_api_keys) : "—", detail: ready ? `${number(data.api_key_calls)} recorded calls` : "Agent access", icon: "apikeys" },
    { label: "Average response", value: avgResponse, detail: "From query logs", icon: "logs" },
  ];
  const commandCards = needsFirstSource ? [
    {id:"docs", icon:"docs", title:"Add your first source", action:"Open Documents"},
    {id:"chat", icon:"chat", title:"Ask after indexing", action:"Open Chat"},
    {id:"integrations", icon:"integrations", title:"Review tool setup", action:"Open Integrations"},
  ] : [
    {id:"chat", icon:"chat", title:"Ask a question", action:"Open Chat"},
    {id:"docs", icon:"docs", title:"Add source material", action:"Open Documents"},
    {id:"integrations", icon:"integrations", title:"Review tool setup", action:"Open Integrations"},
  ];
  const signIn = () => { window.location.hash = "/signin"; };

  return <PageShell className="dashboard-production" title="Dashboard" subtitle="Your recorded activity and next steps."
    actions={<button className="btn btn-outline" disabled={loading} onClick={authenticated ? () => setRevision(r => r + 1) : signIn}>
      <ConsoleIcon name={authenticated ? "refresh" : "users"} size={16}/>{authenticated ? loading ? "Refreshing…" : "Refresh" : "Sign in"}
    </button>}>
    <div className={`int-notice ${error ? "has-error" : ""}`} role={error ? "alert" : "status"}>
      <strong>{error ? "Analytics unavailable" : !authenticated ? "Make this workspace yours" : loading && !ready ? "Loading your activity" : "Your account activity"}</strong>
      <span>{error ? `${error}${ready ? " Showing the last successful response." : ""}` : !authenticated ? "Sign in to see your queries, knowledge bases, and API activity. No sample metrics are shown." : updated ? `Updated ${updated.toLocaleTimeString()} · Metrics are scoped to your account, not the whole organization. They do not verify external service health.` : "Retrieving your recorded activity. External service health is not monitored here."}</span>
    </div>
    <section className="dash-command" aria-label="Dashboard command center">
      <div className="dash-command-copy">
        <span>{needsFirstSource ? "First source" : "Next steps"}</span>
        <h2>{needsFirstSource ? "Start with a source of truth" : authenticated ? "Keep your work moving" : "Preview the operating surface"}</h2>
        <p>{needsFirstSource ? "Create a knowledge base in Documents, upload an approved file, then ask a question when indexing is complete." : authenticated ? "Jump into a useful workflow or review your latest recorded activity." : "Sign in to replace this preview with your own knowledge bases, documents, and activity."}</p>
      </div>
      <div className="dash-command-actions">
        {commandCards.map(item => <button key={item.id} onClick={() => onNavigate(item.id)}>
          <ConsoleIcon name={item.icon} size={20}/>
          <span><strong>{item.title}</strong><small>{item.action}</small></span>
        </button>)}
      </div>
    </section>
    <section className="dash-status-strip" aria-label="Workspace status">
      {statusItems.map(item => <div className={`dash-status-item is-${item.tone}`} key={item.label}>
        <span>{item.label}</span>
        <strong>{item.value}</strong>
      </div>)}
    </section>
    <section className="dash-metrics" aria-label="Workspace metrics" aria-busy={loading}>
      {metrics.map(metric => <div key={metric.label} className="dash-metric">
        <div><span>{metric.label}</span><ConsoleIcon name={metric.icon} size={16}/></div>
        <strong>{metric.value}</strong><small>{metric.detail}</small>
      </div>)}
    </section>
    <div className="dash-columns">
      <section className="dash-panel">
        <header><div><h2>Query volume</h2><p>Your recorded query logs</p></div><span className="dash-meta">{series.length ? `${series.length} recorded days` : "No history"}</span></header>
        {series.length ? <>
          <div className="dash-chart" role="img" aria-label={series.map(row => `${row.date}: ${row.count} queries`).join("; ")}>
            {series.map(row => <div className="dash-bar-slot" key={row.date}><div className="dash-bar" title={`${row.date}: ${row.count} queries`} style={{height:`${Number(row.count) / maxQueries * 100}%`}}/></div>)}
          </div>
          <div className="dash-chart-axis"><span>{series[0].date}</span><span>{series[series.length - 1].date}</span></div>
        </> : <EmptyState icon="analytics" title={needsFirstSource ? "Add sources before your first query" : ready ? "No query history yet" : authenticated ? error ? "Query history unavailable" : "Loading query history" : "Sign in to see your query history"} action={needsFirstSource ? "Open Documents" : ready ? "Open Chat" : authenticated ? error ? "Retry analytics" : null : "Sign in to view"} onAction={needsFirstSource ? () => onNavigate("docs") : ready ? () => onNavigate("chat") : authenticated ? () => setRevision(r => r + 1) : signIn}>
          {needsFirstSource ? "Create a knowledge base and upload an approved document. You can ask when indexing is complete." : ready ? "Your recorded query activity will appear here after your first question." : authenticated ? error ? "Refresh your analytics to check for recorded queries." : "Your query history will appear when analytics finish loading." : "Your query history appears here after sign-in."}
        </EmptyState>}
      </section>
      <section className="dash-panel">
        <header><div><h2>API key activity</h2><p>Usage by keys you created</p></div><button className="dash-text-link" onClick={() => onNavigate("apikeys")}>Manage keys</button></header>
        {keys.length ? <ul className="dash-records">{keys.map(key => <li key={key.id}><div><strong>{key.name}</strong><small>{key.last_used_at ? `Last used ${new Date(key.last_used_at).toLocaleDateString()}` : "Not used yet"}</small></div><span>{number(key.call_count)} calls</span></li>)}</ul> : <EmptyState icon="apikeys" title={ready ? "No API keys yet" : "Keep agent access in view"} action="Manage API keys" onAction={() => onNavigate("apikeys")}>
          {ready ? "Create a key to track requests from your applications." : "Sign in to view saved keys and their recorded usage."}
        </EmptyState>}
      </section>
      <section className="dash-panel">
        <header><div><h2>Knowledge bases</h2><p>Your bases, ranked by recorded queries</p></div><button className="dash-text-link" onClick={() => onNavigate("kb")}>View all</button></header>
        {topKbs.length ? <ul className="dash-records">{topKbs.map((kb, i) => <li key={`${kb.kb}-${i}`}><div className="dash-kb-record"><strong>{kb.kb}</strong><div className="dash-kb-track"><div style={{width:`${Number(kb.queries) / maxKb * 100}%`}}/></div></div><span>{number(kb.queries)}</span></li>)}</ul> : <EmptyState icon="kb" title={ready ? "Build your team's knowledge" : "Your knowledge, connected"} action="Explore knowledge bases" onAction={() => onNavigate("kb")}>
          {ready ? "Create a knowledge base and add documents to get started." : "Sign in to see your own knowledge bases and query counts."}
        </EmptyState>}
      </section>
      <section className="dash-panel dash-next">
        <header><div><h2>Keep work moving</h2><p>A few useful places to start</p></div></header>
        {[
          {id:"docs",title:"Bring your documents",description:"Create a knowledge base and manage your sources."},
          {id:"chat",title:"Ask your sources",description:"Ask questions after your documents are indexed."},
          {id:"integrations",title:"Review tool setup",description:"Browse connectors and their setup requirements."},
        ].map(item => <button className="dash-action-row" key={item.id} onClick={() => onNavigate(item.id)}><ConsoleIcon name={item.id} size={21}/><span><strong>{item.title}</strong><small>{item.description}</small></span><svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="m6 3 5 5-5 5" fill="none" stroke="currentColor"/></svg></button>)}
      </section>
    </div>
  </PageShell>;
}
